import React from "react";
import { SpinnerIcon } from "../assets";
import { useI18n } from "../providers/I18nProvider";
import { useBetSlip } from "../providers/BetSlipProvider";

interface SummaryActionsProps {
  hasOddsChanges: boolean;
  hasClosedOrSuspendedSelections: boolean;
  isPlacingBet: boolean;
  isBookingBet: boolean;
  isAuthorizing: boolean;
  timeoutSecondsLeft?: number;
  isLoggedIn: boolean;
  cultureUrl: string;
  disableFreeBetPlacement?: boolean;
  onPlaceBet: (opts: { isBooking: boolean }) => void;
  onAcceptOddsChanges: () => void;
}

const AcceptOddsButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useI18n();
  return (
    <button
      onClick={onClick}
      className="w-full py-2 px-4 bg-btn-accept-odds text-btn-accept-odds-text text-md rounded-md focus:outline-none"
    >
      {t("BetSlipAcceptChanges")}
    </button>
  );
};

const PlaceBetButtons: React.FC<
  Omit<
    SummaryActionsProps,
    "hasOddsChanges" | "hasClosedOrSuspendedSelections" | "onAcceptOddsChanges"
  >
> = ({ isPlacingBet, isBookingBet, isAuthorizing, timeoutSecondsLeft, isLoggedIn, cultureUrl, disableFreeBetPlacement, onPlaceBet }) => {
  const { t } = useI18n();
  const { onNavigate } = useBetSlip();
  const isDisabled = isPlacingBet || isBookingBet || isAuthorizing || disableFreeBetPlacement;
  return (
    <>
      <button
        onClick={() =>
          isLoggedIn ? onPlaceBet({ isBooking: false }) : onNavigate(`${cultureUrl}/authentication/login`)
        }
        disabled={isDisabled}
        className="w-full py-[6px] px-4 text-md leading-[26px] bg-btn-place-bet text-btn-place-bet-text border border-btn-place-bet-border font-bold rounded-4 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isPlacingBet || isAuthorizing ? (
          <span className="flex items-center justify-center gap-2">
            {t("BetSlipWidgetProcessingBet")}{timeoutSecondsLeft ? ` (${timeoutSecondsLeft})` : ""}
            <SpinnerIcon className="animate-[spin_2s_linear_infinite] au-m-ico-l w-5 h-5" />
          </span>
        ) : isLoggedIn ? (
          t("BetSlipPlaceBet")
        ) : (
          t("BetSlipLoginToBet")
        )}
      </button>

    <button
      onClick={() => onPlaceBet({ isBooking: true })}
      disabled={isDisabled}
      className="w-full py-[6px] px-4 text-md leading-[26px] rounded-4 bg-btn-book-bet font-bold border border-btn-book-bet-border text-btn-book-bet-text disabled:opacity-50 disabled:cursor-not-allowed"
    >
      BOOK A BET
    </button>

      {!isLoggedIn && (
        <p className="text-center text-sm mt-1">
          {t("BetslipWidgetAuthenticationRegisterButtonText")}{" "}
          <button
            type="button"
            onClick={() => onNavigate(`${cultureUrl}/authentication/register`)}
            className="text-betslip-link-text font-semibold underline"
          >
            {t("BetslipWidgetAuthenticationRegisterButtonJoinNowText")}
          </button>
        </p>
      )}
    </>
  );
};

export const SummaryActions: React.FC<SummaryActionsProps> = ({
  hasOddsChanges,
  hasClosedOrSuspendedSelections,
  isPlacingBet,
  isBookingBet,
  isAuthorizing,
  timeoutSecondsLeft,
  isLoggedIn,
  cultureUrl,
  disableFreeBetPlacement,
  onPlaceBet,
  onAcceptOddsChanges,
}) => (
  <div className="space-y-2.5 p-4">
    {hasOddsChanges || hasClosedOrSuspendedSelections ? (
      <AcceptOddsButton onClick={onAcceptOddsChanges} />
    ) : (
      <PlaceBetButtons
        isPlacingBet={isPlacingBet}
        isBookingBet={isBookingBet}
        isAuthorizing={isAuthorizing}
        timeoutSecondsLeft={timeoutSecondsLeft}
        isLoggedIn={isLoggedIn}
        cultureUrl={cultureUrl}
        disableFreeBetPlacement={disableFreeBetPlacement}
        onPlaceBet={onPlaceBet}
      />
    )}
  </div>
);
